import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  Fab,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Mic,
  MicOff,
  Warning,
  Block
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguageContext } from '../contexts/LanguageContext';
import { getImageUrl, getApiUrl } from '../config';
import SEO, { getHomePageStructuredData, getChurchOrganizationData } from '../components/SEO';

const HomePage: React.FC = () => {
  const { t, currentLanguage, detectTextLanguage, setLanguage } = useLanguageContext();
  const [worshipType, setWorshipType] = useState<'personal' | 'daily' | ''>(''); // 예배 타입 선택
  const [prayerText, setPrayerText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);
  const [genderType, setGenderType] = useState<'brother' | 'sister' | ''>('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockMessage, setBlockMessage] = useState('');
  const [inappropriateAlert, setInappropriateAlert] = useState(false);
  const [inappropriateDetails, setInappropriateDetails] = useState('');
  const [violationType, setViolationType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState<string>('');
  // Validation alert modal state
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  // Daily worship issue selection
  const [dailyIssuesOpen, setDailyIssuesOpen] = useState(false);
  const [dailyIssues, setDailyIssues] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      // Cleanup on component unmount
      if (recognition) {
        recognition.stop();
      }
    };
  }, [recognition]);

  // 텍스트 변경시 AI 언어 감지 (디바운스 적용)
  useEffect(() => {
    if (prayerText.trim().length > 10) { // 최소 10자 이상일 때만 감지
      console.log('🔍 언어 감지 시작:', prayerText.substring(0, 50) + '...');
      
      const timer = setTimeout(async () => {
        try {
          console.log('📡 API 호출 시작...');
          const result = await detectTextLanguage(prayerText);
          console.log('📡 API 응답:', result);
          
          if (result) {
            setDetectedLanguage(result.detected_language);
            console.log(`✅ 언어 감지 결과: ${result.language_name} (신뢰도: ${result.confidence})`);
            console.log(`현재 UI 언어: ${currentLanguage}`);
            
            // 신뢰도 임계값을 0.5로 낮춤 (더 민감하게 반응)
            const confidenceThreshold = 0.5;
            
            // 6번 요구사항: 감지된 언어가 한국어가 아니면 영어로 UI 전환
            if (result.detected_language !== 'ko' && result.confidence > confidenceThreshold) {
              console.log(`🌐 한국어가 아닌 언어 감지됨 (${result.detected_language}), UI를 영어로 전환합니다`);
              console.log(`신뢰도: ${result.confidence} > ${confidenceThreshold}`);
              setLanguage('en');
            } else if (result.detected_language === 'ko' && currentLanguage === 'en' && result.confidence > confidenceThreshold) {
              console.log('🌐 한국어 감지됨, UI를 한국어로 전환합니다');
              console.log(`신뢰도: ${result.confidence} > ${confidenceThreshold}`);
              setLanguage('ko');
            } else {
              console.log('⏸️ 언어 전환 조건 미충족:');
              console.log(`- 감지된 언어: ${result.detected_language}`);
              console.log(`- 현재 UI 언어: ${currentLanguage}`);
              console.log(`- 신뢰도: ${result.confidence} (임계값: ${confidenceThreshold})`);
            }
          } else {
            console.log('❌ 언어 감지 결과가 null입니다');
          }
        } catch (error) {
          console.error('❌ 언어 감지 실패:', error);
          // 기본 언어 감지 fallback
          const koreanRegex = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/;
          const detectedLang = koreanRegex.test(prayerText) ? 'ko' : 'en';
          setDetectedLanguage(detectedLang);
          console.log(`🔄 Fallback 언어 감지: ${detectedLang}`);
          
          // fallback 언어 감지 결과에 따라 UI 전환
          if (detectedLang !== currentLanguage) {
            console.log('🌐 fallback 언어 감지 결과에 따라 UI 전환:', detectedLang);
            setLanguage(detectedLang);
          }
        }
      }, 1000); // 1초 디바운스

      return () => clearTimeout(timer);
    } else {
      console.log('⏹️ 텍스트가 너무 짧음 (10자 미만):', prayerText.length);
    }
  }, [prayerText, detectTextLanguage, setLanguage, currentLanguage]);

  // 입력창 blur 시 유효성 검사 실행
  const handlePrayerBlur = () => {
    const text = prayerText.trim();
    if (!text) return;
    if (text.length <= 50) {
      setValidationMessage(t('homepage.alerts.minLength50'));
      setValidationOpen(true);
      return;
    }
    // Gender validation removed - will only check on submit
  };

  // 감지된 언어 기준으로 성별 라벨을 반환 (UI 언어 전환과 무관하게 표시)
  const getGenderLabels = (langCode: string): { brother: string; sister: string } => {
    const map: Record<string, { brother: string; sister: string }> = {
      ko: { brother: '형제님', sister: '자매님' },
      en: { brother: 'Brother', sister: 'Sister' },
      ja: { brother: '兄弟', sister: '姉妹' },
      zh: { brother: '弟兄', sister: '姊妹' },
      es: { brother: 'Hermano', sister: 'Hermana' },
      fr: { brother: 'Frère', sister: 'Sœur' },
      ru: { brother: 'Брат', sister: 'Сестра' },
      it: { brother: 'Fratello', sister: 'Sorella' },
      pt: { brother: 'Irmão', sister: 'Irmã' },
      id: { brother: 'Saudara', sister: 'Saudari' },
      vi: { brother: 'Anh em', sister: 'Chị em' },
      th: { brother: 'พี่ชาย', sister: 'พี่สาว' },
    };
    const key = (langCode || '').toLowerCase();
    return map[key] || map['en'];
  };

  const targetLangForLabels = detectedLanguage || currentLanguage;
  const { brother: brotherLabel, sister: sisterLabel } = getGenderLabels(targetLangForLabels);

  const addPunctuation = (text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText) return text;
    
    const lastChar = trimmedText[trimmedText.length - 1];
    const punctuationMarks = ['.', '!', '?', ':', ';', ','];
    
    if (!punctuationMarks.includes(lastChar)) {
      // 한국어 문장 패턴에 따라 구두점 결정
      if (trimmedText.includes('인가') || trimmedText.includes('까') || 
          trimmedText.includes('니까') || trimmedText.includes('나요') ||
          trimmedText.includes('까요') || trimmedText.includes('어떻게') ||
          trimmedText.includes('언제') || trimmedText.includes('어디') ||
          trimmedText.includes('무엇') || trimmedText.includes('왜')) {
        return trimmedText + '?';
      } else {
        return trimmedText + '.';
      }
    }
    return trimmedText;
  };

  const stopVoiceInput = () => {
    if (recognition) {
      recognition.stop();
      setIsListening(false);
      setRecognition(null);
    }
  };

  const startVoiceInput = () => {
    if (isListening) {
      stopVoiceInput();
      return;
    }

    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      const newRecognition = new SpeechRecognition();
      
      // 현재 UI 언어에 맞게 음성 인식 언어 설정
      newRecognition.lang = currentLanguage === 'ko' ? 'ko-KR' : 'en-US';
      newRecognition.continuous = true;
      newRecognition.interimResults = true;

      newRecognition.onstart = () => {
        setIsListening(true);
      };

      newRecognition.onresult = (event: any) => {
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          }
        }

        if (finalTranscript) {
          const punctuatedText = addPunctuation(finalTranscript);
          setPrayerText(prev => {
            const newText = prev + punctuatedText;
            // 마지막에 공백이 없으면 추가
            return newText.endsWith(' ') ? newText : newText + ' ';
          });
        }
      };

      newRecognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        setRecognition(null);
      };

      newRecognition.onend = () => {
        setIsListening(false);
        setRecognition(null);
      };

      setRecognition(newRecognition);
      newRecognition.start();
    } else {
      alert(t('homepage.alerts.voiceNotSupported'));
    }
  };

  const handleWorshipStart = async () => {
    // 나만을 위한 예배만 처리 (daily는 카드 클릭에서 직접 처리)
    if (!prayerText.trim()) {
      setValidationMessage(t('homepage.alerts.enterPrayer'));
      setValidationOpen(true);
      return;
    }

    // 기도 내용 최소 길이 50자 검증
    if (prayerText.trim().length <= 50) {
      setValidationMessage(t('homepage.alerts.minLength50'));
      setValidationOpen(true);
      return;
    }

    if (!genderType) {
      setValidationMessage(t('homepage.alerts.selectGenderStrict'));
      setValidationOpen(true);
      return;
    }

    setIsSubmitting(true);

    try {
      // Save prayer to MongoDB and get ID
      const response = await fetch(getApiUrl('prayers/anonymous'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: prayerText }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if it's inappropriate content
        if (data.error === 'INAPPROPRIATE_CONTENT') {
          // 즉시 alert으로 알림
          alert(t('homepage.alerts.inappropriateContent'));
          
          setViolationType(data.violation_type || '부적절한 내용');
          setInappropriateDetails(data.details || '부적절한 내용이 감지되었습니다.');
          setInappropriateAlert(true);
          
          // IP가 차단된 경우
          if (data.ip_blocked) {
            setIsBlocked(true);
            setBlockMessage(data.details);
          }
          
          setIsSubmitting(false);
          return;
        }
        
        // Check if IP is blocked
        if (data.error === 'IP_BLOCKED') {
          // Alert box 먼저 표시
          alert(t('homepage.alerts.ipBlocked'));
          
          // 그 다음 상세 Dialog 표시
          setIsBlocked(true);
          setBlockMessage(data.details || '접근이 차단되었습니다.');
          setIsSubmitting(false);
          return;
        }
        
        throw new Error(data.error || 'Failed to save prayer');
      }

      const prayerId = data.prayer_id;

      // Navigate with prayer ID, gender type, and detected language
      navigate('/worship', { 
        state: { 
          worshipType: 'personal',
          prayerId,
          genderType,
          detectedLanguage: detectedLanguage || currentLanguage
        }
      });
    } catch (error) {
      console.error('Error saving prayer:', error);
      
      // Check if it's a network error that might indicate IP blocking
      if (error instanceof TypeError && error.message.includes('fetch')) {
        // Fetch 자체가 실패한 경우 (네트워크 에러 또는 차단)
        alert(t('homepage.alerts.networkBlocked'));
      } else if (!isBlocked) {
        // 일반적인 에러의 경우
        alert(t('homepage.alerts.networkError'));
      }
      
      setIsSubmitting(false);
    }
  };

  const handleInappropriateClose = () => {
    setInappropriateAlert(false);
    setPrayerText(''); // Clear the inappropriate content
  };

  const handleBlockedClose = () => {
    setIsBlocked(false);
    // Don't close immediately, user needs to see the message
  };

  const handleIssueSelect = (selectedIssue: any) => {
    setDailyIssuesOpen(false);
    
    // 선택한 이슈로 예배 페이지로 이동
    navigate('/worship', { 
      state: { 
        worshipType: 'daily',
        dailyWorship: {
          issue_title: selectedIssue.issue_title,
          issue_description: selectedIssue.issue_description,
          prayer_points: selectedIssue.prayer_points || [],
          worship_order: selectedIssue.worship_order
        },
        detectedLanguage: currentLanguage
      }
    });
  };

  // 오늘의 기도 제목 선택 - 히어로 화면 대신 전체 페이지로 표시
  if (dailyIssuesOpen) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: 4,
              background: 'transparent',
              border: '2px solid white',
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                textAlign: 'center',
                fontWeight: 'bold',
                color: 'white',
                borderBottom: '2px solid rgba(255, 255, 255, 0.6)',
                pb: 2,
                mb: 3,
              }}
            >
              {currentLanguage === 'ko' ? '오늘 우리를 위한 기도 제목' : 'Today\'s Prayer Topics for Us'}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                textAlign: 'center',
                fontWeight: 'semibold',
                color: 'white',
              }}
            >
              {(() => {
                const today = new Date();
                const year = today.getFullYear();
                const month = today.getMonth() + 1;
                const day = today.getDate();
                return currentLanguage === 'ko'
                  ? `${year}년 ${month}월 ${day}일 오늘 우리를 위해 예배 드리세요`
                  : `${year}.${month}.${day} Worship with us today`;
              })()}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 3, textAlign: 'center', color: 'rgba(255, 255, 255, 0.85)' }}
            >
              {currentLanguage === 'ko'
                ? '기도하고 싶은 제목을 선택해주세요'
                : 'Please select a prayer topic'}
            </Typography>
            <Grid container spacing={2}>
              {dailyIssues.map((issue, index) => (
                <Grid item xs={12} key={index}>
                  <Card
                    onClick={() => handleIssueSelect(issue)}
                    sx={{
                      cursor: 'pointer',
                      p: 2,
                      border: '2px solid',
                      borderColor: 'rgba(255, 255, 255, 0.6)',
                      background: 'transparent',
                      boxShadow: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'white',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          minWidth: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          border: '1px solid white',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '1rem'
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {issue.is_liturgical && (
                            <Chip
                              label={currentLanguage === 'ko' ? '절기 예배' : 'Seasonal Service'}
                              size="small"
                              sx={{
                                backgroundColor: '#8b5a2b',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: '22px',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          )}
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 'bold',
                              color: 'white',
                              fontSize: '1.1rem'
                            }}
                          >
                            {issue.issue_title}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ lineHeight: 1.6, color: 'rgba(255, 255, 255, 0.85)' }}
                        >
                          {issue.issue_description}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setDailyIssuesOpen(false);
                  setWorshipType(''); // Reset worship type to show selection cards again
                }}
                variant="outlined"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                {currentLanguage === 'ko' ? '돌아가기' : 'Go Back'}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
          <Box>
      <SEO 
        title={currentLanguage === 'ko' ? '맞춤형 온라인 예배' : 'Personalized Online Worship'}
        description={currentLanguage === 'ko' 
          ? '당신의 기도에 귀 기울이는 맞춤형 기독교 예배 서비스. AI 기반 개인화된 설교, 찬송가, 성경 말씀을 경험하세요.'
          : 'Personalized Christian worship service that listens to your prayers. Experience AI-powered personalized sermons, hymns, and scripture.'}
        url="/"
        locale={currentLanguage === 'ko' ? 'ko_KR' : 'en_US'}
        structuredData={{
          '@graph': [
            getHomePageStructuredData(currentLanguage as 'ko' | 'en'),
            getChurchOrganizationData(currentLanguage as 'ko' | 'en')
          ]
        }}
      />
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          pt: { xs: 3, md: 5 },
          pb: { xs: 3, md: 5 },
          // 모바일 첫 화면: 브라우저 하단 막대를 뺀 보이는 높이에 카드를 맞춘다
          ...(!worshipType && {
            '@media (max-width:600px)': {
              height: '100svh',
              minHeight: '100svh',
              maxHeight: '100svh',
              alignItems: 'stretch',
              boxSizing: 'border-box',
              overflow: 'hidden',
              pt: 1.25,
              pb: 1.25,
              px: 1.5,
            },
          }),
        }}
      >
        <Container
          maxWidth="md"
          sx={{
            position: 'relative',
            zIndex: 1,
            mb: { xs: 4, md: 8 },
            ...(!worshipType && {
              '@media (max-width:600px)': {
                mb: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                px: '0 !important',
                maxWidth: '100%',
              },
            }),
          }}
        >
          <Card
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              background: 'transparent',
              border: '2px solid white',
              boxShadow: 'none',
              textAlign: 'center',
              color: 'white',
              '& .MuiTypography-root': {
                color: 'white'
              },
              '& .MuiTextField-root .MuiInputBase-input': {
                color: 'white'
              },
              ...(!worshipType && {
                '@media (max-width:600px)': {
                  p: 2,
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-evenly',
                  overflow: 'hidden',
                },
              }),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                ...(!worshipType && {
                  '@media (max-width:600px)': { mb: 0 },
                }),
              }}
            >
              <Box
                component="img"
                src={getImageUrl('hands.png')}
                alt="기도하는 손"
                sx={{
                  width: 60,
                  height: 60,
                  mr: 2,
                  filter: 'brightness(0) invert(1)',
                  ...(!worshipType && {
                    '@media (max-width:600px)': {
                      width: 40,
                      height: 40,
                      mr: 1.25,
                    },
                  }),
                }}
              />
              <Typography 
                variant="h2" 
                component="h1"
                sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  ...(!worshipType && {
                    '@media (max-width:600px)': {
                      fontSize: 'min(2rem, calc((100vw - 110px) / 8))',
                      lineHeight: 1.15,
                      whiteSpace: 'nowrap',
                    },
                  }),
                }}
              >
                He Listens to Me
              </Typography>
            </Box>
            
            <Box
              sx={{
                mb: 3,
                mt: 3,
                textAlign: 'center',
                ...(!worshipType && {
                  '@media (max-width:600px)': { mb: 0, mt: 0 },
                }),
              }}
            >
              <Typography 
                variant="h5" 
                color="text.secondary"
                sx={{ 
                  fontSize: { xs: '1rem', md: '1.5rem' },
                  fontFamily: '"Nunito Sans", "Gowun Dodum", "Roboto", "Helvetica", "Arial", sans-serif',
                  ...(!worshipType && {
                    '@media (max-width:600px)': currentLanguage === 'ko'
                      ? {
                          fontSize: 'min(1rem, calc((100vw - 64px) / 19.5))',
                          lineHeight: 1.35,
                          whiteSpace: 'nowrap',
                        }
                      : { fontSize: '0.78rem', lineHeight: 1.35 },
                  }),
                }}
              >
                {t('homepage.prayerPurpose')}
              </Typography>
            </Box>

            {/* Worship Type Selection - Show only when no type is selected */}
            {!worshipType && (
            <Box
              sx={{
                mb: 4,
                textAlign: 'center',
                '@media (max-width:600px)': { mb: 0 },
              }}
            >
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  mb: 2, 
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  '@media (max-width:600px)': {
                    mb: 1,
                    fontSize: '0.9rem',
                    lineHeight: 1.35,
                  },
                }}
              >
                {currentLanguage === 'ko' ? '누구를 위한 예배를 드리시겠습니까?' : 'Choose Your Worship Type'}
              </Typography>
              <Grid container spacing={{ xs: 1.25, md: 2 }} justifyContent="center">
                <Grid item xs={12} md={6}>
                  <Card
                    onClick={async () => {
                      setWorshipType('daily');
                      // 바로 오늘의 기도 제목 Dialog 열기
                      setIsSubmitting(true);
                      try {
                        const response = await fetch(getApiUrl(`daily-worship/by-date?language=${currentLanguage}`), {
                          method: 'GET',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                        });

                        const data = await response.json();

                        if (!response.ok || !data.success) {
                          if (response.status === 404) {
                            alert(t('homepage.alerts.dailyWorshipNotAvailable'));
                          } else {
                            alert(currentLanguage === 'ko' 
                              ? '오늘의 예배를 불러오는데 실패했습니다. 잠시 후 다시 시도해주세요.' 
                              : 'Failed to load today\'s worship. Please try again later.');
                          }
                          setIsSubmitting(false);
                          setWorshipType('');
                          return;
                        }

                        const worship = data.worship;
                        if (worship && worship.issues && worship.issues.length > 0) {
                          setDailyIssues(worship.issues);
                          setDailyIssuesOpen(true);
                        } else {
                          alert(currentLanguage === 'ko' 
                            ? '오늘의 기도 제목이 아직 준비되지 않았습니다.' 
                            : 'Today\'s prayer topics are not ready yet.');
                          setWorshipType('');
                        }
                        setIsSubmitting(false);
                      } catch (error) {
                        console.error('Error fetching daily worship:', error);
                        alert(currentLanguage === 'ko' 
                          ? '네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.' 
                          : 'Network error occurred. Please check your internet connection.');
                        setIsSubmitting(false);
                        setWorshipType('');
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      p: 3,
                      border: '2px solid',
                      borderColor: 'grey.300',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      transition: 'all 0.3s ease',
                      '&& .MuiTypography-root': {
                        color: 'white'
                      },
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
                      },
                      '@media (max-width:600px)': {
                        p: 1.5,
                      },
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        mb: 1,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '@media (max-width:600px)': {
                          fontSize: '1rem',
                          lineHeight: 1.25,
                          mb: 0.5,
                          gap: 0.75,
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src="/images/bible.png"
                        alt=""
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          '@media (max-width:600px)': { width: 26, height: 26 },
                        }}
                      />
                      {currentLanguage === 'ko' ? '오늘 우리를 위한 예배' : 'Today\'s Worship'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        '@media (max-width:600px)': { fontSize: '0.78rem', lineHeight: 1.35 },
                      }}
                    >
                      {currentLanguage === 'ko' 
                        ? '오늘 하루 우리 모두를 위해 기도합니다' 
                        : 'We pray for all of us today'}
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card
                    onClick={() => setWorshipType('personal')}
                    sx={{
                      cursor: 'pointer',
                      p: 3,
                      border: '2px solid',
                      borderColor: 'grey.300',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      transition: 'all 0.3s ease',
                      '&& .MuiTypography-root': {
                        color: 'white'
                      },
                      '&:hover': {
                        borderColor: 'secondary.main',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
                      },
                      '@media (max-width:600px)': {
                        p: 1.5,
                      },
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        mb: 1,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '@media (max-width:600px)': {
                          fontSize: '1rem',
                          lineHeight: 1.25,
                          mb: 0.5,
                          gap: 0.75,
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src="/images/praying.png"
                        alt=""
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          '@media (max-width:600px)': { width: 26, height: 26 },
                        }}
                      />
                      {currentLanguage === 'ko' ? '지금 나만을 위한 예배' : 'Personal Worship'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        '@media (max-width:600px)': { fontSize: '0.78rem', lineHeight: 1.35 },
                      }}
                    >
                      {currentLanguage === 'ko' 
                        ? '나만의 아픔, 감사, 소원을 기도하세요' 
                        : 'Pray for your own pain, gratitude, and wishes'}
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Box>
            )}

            {/* Prayer Input Area - Show only for personal worship */}
            {worshipType === 'personal' && (
            <>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={prayerText}
                  onChange={(e) => setPrayerText(e.target.value)}
                  onBlur={handlePrayerBlur}
                  placeholder={t('homepage.prayerPlaceholder')}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: 'white',
                      },
                      '&:hover fieldset': {
                        borderColor: 'white',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'white',
                      },
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: 'white',
                      opacity: 1,
                    }
                  }}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    textAlign: 'right', 
                    mt: 1
                  }}
                >
                  <Box 
                    component="span" 
                    sx={{ color: prayerText.length < 50 ? 'error.main' : 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {prayerText.length}
                  </Box>
                  <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {t('homepage.characterCount')}
                  </Box>
                </Typography>
              </Box>

              {/* Voice Input Button — hidden on mobile (width <= 768px) */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3,
                  '@media (max-width:768px)': {
                    display: 'none',
                  },
                }}
              >
                <Fab
                  color={isListening ? 'error' : undefined}
                  onClick={startVoiceInput}
                  disabled={isListening}
                  sx={{
                    width: 80,
                    height: 80,
                    ...(!isListening && {
                      backgroundColor: 'rgba(109, 76, 65, 0.4)',
                      color: 'white',
                      border: '2px solid white',
                      '&:hover': {
                        backgroundColor: 'rgba(93, 64, 55, 0.4)',
                      },
                    }),
                    animation: isListening ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                        boxShadow: '0 0 0 0 rgba(109, 76, 65, 0.7)',
                      },
                      '70%': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 0 0 10px rgba(109, 76, 65, 0)',
                      },
                      '100%': {
                        transform: 'scale(1)',
                        boxShadow: '0 0 0 0 rgba(109, 76, 65, 0)',
                      },
                    },
                  }}
                >
                  {isListening ? <MicOff /> : <Mic />}
                </Fab>
                <Typography color="text.secondary">
                  {isListening ? t('homepage.voiceInstructions.stop') : t('homepage.voiceInstructions.start')}
                </Typography>
              </Box>
            </>
            )}

            {/* Gender Selection - Show only for personal worship */}
            {worshipType === 'personal' && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 1, md: 1 } }}>
                <Typography 
                  variant="h5" 
                  color="text.secondary" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    flexWrap: 'wrap', 
                    gap: 1,
                    fontSize: { xs: '1rem', md: '1.5rem' }
                  }}
                >
                  {genderType ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {currentLanguage === 'en' && (
                        <Box component="span" sx={{ color: 'white', fontSize: { xs: '1rem', md: '1.5rem' } }}>For</Box>
                      )}
                      <Chip
                        label={genderType === 'brother' ? brotherLabel : sisterLabel}
                        onClick={() => setGenderType('')}
                        variant="filled"
                        sx={{
                          fontSize: { xs: '1rem', md: '1.25rem' },
                          height: { xs: '32px', md: '36px' },
                          border: '2px solid white',
                          color: 'white',
                          backgroundColor: 'rgba(109, 76, 65, 0.4)',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(93, 64, 55, 0.4)'
                          }
                        }}
                      />
                      <Box component="span" sx={{ color: 'white', fontSize: { xs: '1rem', md: '1.5rem' } }}>
                        {currentLanguage === 'en' ? '' : '을 위해'}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                      {currentLanguage === 'en' && (
                        <Box component="span" sx={{ color: 'white', fontSize: { xs: '1rem', md: '1.5rem' } }}>For</Box>
                      )}
                      <Chip
                        label={brotherLabel}
                        onClick={() => setGenderType('brother')}
                        variant="outlined"
                        sx={{
                          fontSize: { xs: '1rem', md: '1.25rem' },
                          height: { xs: '32px', md: '36px' },
                          border: '2px solid white',
                          color: 'white',
                          backgroundColor: 'rgba(109, 76, 65, 0.4)',
                          cursor: 'pointer',
                           animation: 'genderPulse 2.2s ease-in-out infinite',
                          '&:hover': {
                            backgroundColor: 'rgba(93, 64, 55, 0.4)',
                            color: 'white',
                            animation: 'none'
                          },
                          '@keyframes genderPulse': {
                             '0%, 60%, 100%': {
                               transform: 'scale(1)',
                               boxShadow: '0 0 0 0 rgba(109, 76, 65, 0.45)'
                             },
                             '30%': {
                               transform: 'scale(1.1)',
                               boxShadow: '0 0 0 14px rgba(109, 76, 65, 0)'
                             }
                          }
                        }}
                      />
                      <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                        /
                      </Box>
                      <Chip
                        label={sisterLabel}
                        onClick={() => setGenderType('sister')}
                        variant="outlined"
                        sx={{
                          fontSize: { xs: '1rem', md: '1.25rem' },
                          height: { xs: '32px', md: '36px' },
                          border: '2px solid white',
                          color: 'white',
                          backgroundColor: 'rgba(109, 76, 65, 0.4)',
                          cursor: 'pointer',
                           animation: 'genderPulse 2.2s ease-in-out infinite 0.5s',
                          '&:hover': {
                            backgroundColor: 'rgba(93, 64, 55, 0.4)',
                            color: 'white',
                            animation: 'none'
                          },
                          '@keyframes genderPulse': {
                             '0%, 60%, 100%': {
                               transform: 'scale(1)',
                               boxShadow: '0 0 0 0 rgba(109, 76, 65, 0.45)'
                             },
                             '30%': {
                               transform: 'scale(1.1)',
                               boxShadow: '0 0 0 14px rgba(109, 76, 65, 0)'
                             }
                          }
                        }}
                      />
                      <Box component="span" sx={{ color: 'white', fontSize: { xs: '1rem', md: '1.5rem' } }}>
                        {currentLanguage === 'en' ? '' : '을 위해'}
                      </Box>
                    </Box>
                  )}
                </Typography>
              </Box>

              {/* Start Personal Worship Button */}
              <Button
                variant="contained"
                size="large"
                onClick={handleWorshipStart}
                disabled={isSubmitting || !prayerText.trim() || !genderType}
                sx={{
                  mt: 3,
                  px: 6,
                  py: 2,
                  borderRadius: 3,
                  fontSize: '1.2rem',
                  background: (!isSubmitting && prayerText.trim() && genderType)
                    ? 'linear-gradient(45deg, #8b7355, #a0956b)'
                    : 'rgba(255, 255, 255, 0.15)',
                  color: (!isSubmitting && prayerText.trim() && genderType) ? 'white' : 'rgba(255, 255, 255, 0.5)',
                  '&:hover': (!isSubmitting && prayerText.trim() && genderType) ? {
                    background: 'linear-gradient(45deg, #7a6448, #8f8058)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
                  } : {},
                  '&:disabled': {
                    background: 'rgba(255, 255, 255, 0.15)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {isSubmitting ? t('homepage.startWorshipButton.loading') : t('homepage.startWorshipButton.ready')}
              </Button>
            </Box>
            )}

            {/* Privacy Notice */}
            <Alert
              icon={false}
              severity="info" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                backgroundColor: 'rgba(25, 118, 210, 0.05)',
                '& .MuiAlert-message': {
                  fontSize: '0.95rem',
                  textAlign: 'center',
                  width: '100%',
                  ...(!worshipType && {
                    '@media (max-width:600px)': {
                      fontSize: '0.72rem',
                    },
                  }),
                },
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  marginTop: '2px'
                },
                ...(!worshipType && {
                  '@media (max-width:600px)': {
                    mb: 0,
                    py: 0.75,
                  },
                }),
              }}
            >
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: '600',
                    mb: 0.5,
                    ...(!worshipType && {
                      '@media (max-width:600px)': {
                        fontSize: '0.75rem',
                        lineHeight: 1.35,
                        mb: 0.25,
                      },
                    }),
                  }}
                >
                  {t('homepage.privacyNotice.title')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-line',
                    ...(!worshipType && {
                      '@media (max-width:600px)': {
                        fontSize: '0.72rem',
                        lineHeight: 1.35,
                      },
                    }),
                  }}
                >
                  {t('homepage.privacyNotice.content')}
                </Typography>
              </Box>
            </Alert>

            {worshipType === 'personal' && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  onClick={() => {
                    stopVoiceInput();
                    setWorshipType('');
                  }}
                  variant="outlined"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  {t('homepage.backToHome')}
                </Button>
              </Box>
            )}

          </Card>
        </Container>
      </Box>

      {/* About Section */}
      <Box sx={{ py: { xs: 8, md: 15 }, backgroundColor: 'transparent' }}>
        <Container>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
              {t('homepage.features.title')}
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', color: 'white' }}>
              {t('homepage.features.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  textAlign: 'center',
                  background: 'transparent',
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: '#d4c4a8',
                  borderRadius: 3,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: '#d4c4a8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      position: 'relative',
                      overflow: 'visible',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '15px',
                        right: '15px',
                        width: '35px',
                        height: '35px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0.1) 70%, transparent 100%)',
                        zIndex: 3,
                        animation: 'sparkle1 4s ease-in-out infinite',
                        boxShadow: '0 0 30px 15px rgba(255,255,255,0.2), 0 0 45px 20px rgba(255,255,255,0.1), 0 0 60px 25px rgba(255,255,255,0.05)',
                        filter: 'blur(2px)',
                      },
                      '@keyframes sparkle1': {
                        '0%, 80%, 100%': { 
                          opacity: 0,
                          transform: 'scale(0.3)',
                        },
                        '40%': { 
                          opacity: 1,
                          transform: 'scale(1)',
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={getImageUrl('bible.png')}
                      alt="공동체를 위한 예배"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%',
                        filter: 'sepia(20%) saturate(80%) brightness(1.1) drop-shadow(2px 4px 8px rgba(0, 0, 0, 0.3)) drop-shadow(1px 2px 4px rgba(0, 0, 0, 0.2))',
                        zIndex: 1,
                        position: 'relative',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
                    {t('homepage.features.feature1.title')}
                  </Typography>
                  <Typography sx={{ color: 'white' }}>
                    {t('homepage.features.feature1.description')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  textAlign: 'center',
                  background: 'transparent',
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: '#c9bfb2',
                  borderRadius: 3,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: '#c9bfb2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      position: 'relative',
                      overflow: 'visible',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '15px',
                        left: '15px',
                        width: '35px',
                        height: '35px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0.1) 70%, transparent 100%)',
                        zIndex: 3,
                        animation: 'sparkle2 4s ease-in-out infinite 1.3s',
                        boxShadow: '0 0 30px 15px rgba(255,255,255,0.2), 0 0 45px 20px rgba(255,255,255,0.1), 0 0 60px 25px rgba(255,255,255,0.05)',
                        filter: 'blur(2px)',
                      },
                      '@keyframes sparkle2': {
                        '0%, 80%, 100%': { 
                          opacity: 0,
                          transform: 'scale(0.3)',
                        },
                        '40%': { 
                          opacity: 1,
                          transform: 'scale(1)',
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={getImageUrl('praying.png')}
                      alt="개인 맞춤 예배"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%',
                        filter: 'sepia(20%) saturate(80%) brightness(1.1) drop-shadow(2px 4px 8px rgba(0, 0, 0, 0.3)) drop-shadow(1px 2px 4px rgba(0, 0, 0, 0.2))',
                        zIndex: 1,
                        position: 'relative',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
                    {t('homepage.features.feature2.title')}
                  </Typography>
                  <Typography sx={{ color: 'white' }}>
                    {t('homepage.features.feature2.description')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  textAlign: 'center',
                  background: 'transparent',
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: '#bfb5a6',
                  borderRadius: 3,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: '#bfb5a6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      position: 'relative',
                      overflow: 'visible',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '15px',
                        left: '15px',
                        width: '35px',
                        height: '35px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0.1) 70%, transparent 100%)',
                        zIndex: 3,
                        animation: 'sparkle3 4s ease-in-out infinite 2.6s',
                        boxShadow: '0 0 30px 15px rgba(255,255,255,0.2), 0 0 45px 20px rgba(255,255,255,0.1), 0 0 60px 25px rgba(255,255,255,0.05)',
                        filter: 'blur(2px)',
                      },
                      '@keyframes sparkle3': {
                        '0%, 80%, 100%': { 
                          opacity: 0,
                          transform: 'scale(0.3)',
                        },
                        '40%': { 
                          opacity: 1,
                          transform: 'scale(1)',
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={getImageUrl('service.png')}
                      alt="완전한 예배"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%',
                        filter: 'sepia(20%) saturate(80%) brightness(1.1) drop-shadow(2px 4px 8px rgba(0, 0, 0, 0.3)) drop-shadow(1px 2px 4px rgba(0, 0, 0, 0.2))',
                        zIndex: 1,
                        position: 'relative',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
                    {t('homepage.features.feature3.title')}
                  </Typography>
                  <Typography sx={{ color: 'white' }}>
                    {t('homepage.features.feature3.description')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Validation Alert Dialog */}
      <Dialog
        open={validationOpen}
        onClose={() => setValidationOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('homepage.validation.title')}</DialogTitle>
        <DialogContent>
          <Typography>{validationMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationOpen(false)} variant="contained" color="primary">
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 부적절한 내용 감지 알림 Dialog */}
      <Dialog
        open={inappropriateAlert}
        onClose={handleInappropriateClose}
        maxWidth="md"
        fullWidth
        disableScrollLock
        disableRestoreFocus
        disableEnforceFocus
        keepMounted={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'error.light',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center',
          bgcolor: 'error.main',
          color: 'white',
          fontWeight: 'bold'
        }}>
          <Warning sx={{ mr: 2, fontSize: 30 }} />
          {t('homepage.dialogs.inappropriateContent.title')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2, color: 'text.primary' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>{t('homepage.dialogs.inappropriateContent.detectedProblem')}</strong> {violationType}
          </Alert>
          <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {inappropriateDetails}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('homepage.dialogs.inappropriateContent.description')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleInappropriateClose}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ py: 1.5 }}
          >
            {t('homepage.dialogs.inappropriateContent.understand')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* IP 차단 알림 Dialog */}
      <Dialog
        open={isBlocked}
        onClose={handleBlockedClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        disableScrollLock
        disableRestoreFocus
        disableEnforceFocus
        keepMounted={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'grey.900',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center',
          bgcolor: 'error.dark',
          color: 'white',
          fontWeight: 'bold'
        }}>
          <Block sx={{ mr: 2, fontSize: 30 }} />
          {t('homepage.dialogs.ipBlocked.title')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2, color: 'white' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>{t('homepage.dialogs.ipBlocked.subtitle')}</strong>
          </Alert>
          <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
            {blockMessage}
          </Typography>
          <Box sx={{ 
            p: 2, 
            bgcolor: 'grey.800', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.600'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('homepage.dialogs.ipBlocked.emailContact')}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              support@pray4me.com
            </Typography>
            <Typography variant="caption" color="grey.400" sx={{ display: 'block', mt: 1 }}>
              {t('homepage.dialogs.ipBlocked.emailInstructions')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => window.location.href = 'mailto:support@pray4me.com?subject=[차단해제요청] 접근 차단 해제 요청'}
            variant="contained"
            color="primary"
            sx={{ mr: 2 }}
          >
            {t('homepage.dialogs.ipBlocked.emailButton')}
          </Button>
          <Button 
            onClick={handleBlockedClose}
            variant="outlined"
            color="inherit"
            sx={{ color: 'white', borderColor: 'grey.600' }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default HomePage; 